'use client';

import Image from 'next/image';
import ButtonGroup from '@/components/blocks/ButtonGroup';
import Container from '@/components/ui/container';
import { cn, parseHeading, sanitizeHtml } from '@/lib/utils';
import { getDirectusAssetURL } from '@/lib/directus/directus-utils';
import { setAttr } from '@directus/visual-editing';
import { DirectusFile } from '@/types/directus-schema';

interface ContentImageProps {
	data: {
		headline: string;
		content: string;
		image?: DirectusFile;
		image_position?: 'left' | 'right';
		background_color?: string;
		button_group?: {
			id: string;
			buttons: any[];
		};
	};
	itemId?: string;
}

const ContentImage = ({ data, itemId }: ContentImageProps) => {
	const { headline, content, image, image_position = 'left', background_color, button_group } = data;

	const isImageRight = image_position === 'right';

	const bgClass =
		background_color === 'gray'
			? 'bg-gray-50'
			: background_color === 'blue'
				? 'bg-blue text-white'
				: background_color === 'dark'
					? 'bg-gray-900 text-white'
					: 'bg-white';

	const bgType =
		background_color === 'orange'
			? 'orange'
			: background_color === 'blue'
				? 'blue'
				: background_color === 'dark'
					? 'dark'
					: 'light';

	// adjust text color if background is dark
	const textColor = ['blue', 'dark'].includes(background_color || '') ? 'text-white' : 'text-blue-dark';
	const proseColor = ['blue', 'dark'].includes(background_color || '') ? 'text-gray-100' : 'text-gray-dark';

	return (
		<section className={cn('py-16 md:py-24', bgClass)} data-directus-id={itemId}>
			<Container>
				<div className={cn('flex flex-col gap-12 items-center', isImageRight ? 'md:flex-row-reverse' : 'md:flex-row')}>
					{/* Image Column */}
					<div className="w-full md:w-5/12">
						{image && (
							<div className="relative aspect-video md:aspect-[4/3] w-full overflow-hidden rounded-lg">
								<Image
									src={getDirectusAssetURL(image)}
									alt={image.description || image.title || headline || 'Content image'}
									fill
									className="object-cover"
									{...(itemId
										? {
												'data-directus': setAttr({
													collection: 'block_content_image',
													item: itemId,
													fields: ['image'],
												}),
											}
										: {})}
								/>
							</div>
						)}
					</div>

					{/* Content Column */}
					<div className="w-full md:w-7/12 flex flex-col justify-center space-y-6 md:px-20">
						<h2
							className={cn('text-3xl md:text-4xl font-heading', textColor)}
							{...(itemId
								? {
										'data-directus': setAttr({
											collection: 'block_content_image',
											item: itemId,
											fields: ['headline'],
										}),
									}
								: {})}
						>
							{parseHeading(headline)}
						</h2>

						<div
							className={cn('prose max-w-none', proseColor)}
							{...(itemId
								? {
										'data-directus': setAttr({
											collection: 'block_content_image',
											item: itemId,
											fields: ['content'],
										}),
									}
								: {})}
							dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
						/>

						{button_group && (
							<div
								className="pt-2"
								{...(itemId
									? {
											'data-directus': setAttr({
												collection: 'block_content_image',
												item: itemId,
												fields: ['button_group'],
											}),
										}
									: {})}
							>
								<ButtonGroup
									buttons={button_group.buttons?.map((b: any) => b.block_button_id).filter(Boolean)}
									theme={bgType as any}
								/>
							</div>
						)}
					</div>
				</div>
			</Container>
		</section>
	);
};

export default ContentImage;
